export type AppRole =
  | 'CLIENTE'
  | 'CONSULTA'
  | 'CONSULTOR_SAI'
  | 'PROVEEDOR'
  | 'OFICINA'
  | 'RH'
  | 'VENTAS_INTERNAS'
  | 'COBRANZA'
  | 'GERENTE_LOGISTICA'
  | 'MKT'
  | 'ALMACENISTA'
  | 'CHOFER'
  | 'GERENTE_INV'
  | 'SUPERVISOR'
  | 'OPERADOR'
  | 'ADMIN'
  | 'VENDEDOR';
export type Role = AppRole;

export type AppCapabilities = {
  canViewInventory: boolean;
  canUseOperations: boolean;
  canViewActivities: boolean;
  canViewInbox: boolean;
  canViewDashboard: boolean;
  canUseAgent: boolean;
  canUseRoutes: boolean;
  canViewAdminUsers: boolean;
  canViewAdminRh: boolean;
  canViewAdminReports: boolean;
  canViewAdminInventory: boolean;
  canViewAdminCotizaciones: boolean;
  canManageHomeContent: boolean;
  canManageSalesContent: boolean;
  canViewProviderAgenda: boolean;
  canViewClientPortal: boolean;
  canManageClientQuoteIntake: boolean;
  canManageClientBilling: boolean;
};

export const defaultCapabilities: AppCapabilities = {
  canViewInventory: false,
  canUseOperations: false,
  canViewActivities: false,
  canViewInbox: false,
  canViewDashboard: false,
  canUseAgent: false,
  canUseRoutes: false,
  canViewAdminUsers: false,
  canViewAdminRh: false,
  canViewAdminReports: false,
  canViewAdminInventory: false,
  canViewAdminCotizaciones: false,
  canManageHomeContent: false,
  canManageSalesContent: false,
  canViewProviderAgenda: false,
  canViewClientPortal: false,
  canManageClientQuoteIntake: false,
  canManageClientBilling: false,
};

export function normalizeRole(role?: string | null): AppRole {
  const normalized = (role ?? '').trim().toUpperCase();
  if (normalized === 'ADMIN') return 'ADMIN';
  if (normalized === 'GERENTE_INV') return 'GERENTE_INV';
  if (normalized === 'SUPERVISOR') return 'SUPERVISOR';
  if (normalized === 'CONSULTA') return 'CONSULTA';
  if (normalized === 'CONSULTOR_SAI' || normalized === 'CONSULTOR SAI' || normalized === 'CONSULTOR-SAI') {
    return 'CONSULTOR_SAI';
  }
  if (normalized === 'PROVEEDOR') return 'PROVEEDOR';
  if (normalized === 'CLIENTE') return 'CLIENTE';
  if (normalized === 'OFICINA') return 'OFICINA';
  if (normalized === 'RH' || normalized === 'RECURSOS HUMANOS' || normalized === 'RECURSOS-HUMANOS') return 'RH';
  if (normalized === 'VENTAS_INTERNAS' || normalized === 'VENTAS INTERNAS' || normalized === 'VENTAS-INTERNAS') {
    return 'VENTAS_INTERNAS';
  }
  if (normalized === 'COBRANZA') return 'COBRANZA';
  if (
    normalized === 'GERENTE_LOGISTICA' ||
    normalized === 'GERENTE LOGISTICA' ||
    normalized === 'GERENTE LOGÍSTICA' ||
    normalized === 'GERENTE LOGISTICO' ||
    normalized === 'GERENTE LOGÍSTICO' ||
    normalized === 'GERENTE LOGOSTICA' ||
    normalized === 'GERENTE DE LOGISTICA' ||
    normalized === 'GERENTE DE LOGÍSTICA' ||
    normalized === 'LOGISTICA' ||
    normalized === 'LOGÍSTICA'
  ) {
    return 'GERENTE_LOGISTICA';
  }
  if (normalized === 'MKT') return 'MKT';
  if (normalized === 'ALMACENISTA') return 'ALMACENISTA';
  if (normalized === 'CHOFER') return 'CHOFER';
  if (normalized === 'OPERADOR') return 'OPERADOR';
  if (normalized === 'VENDEDOR') return 'VENDEDOR';
  return 'ALMACENISTA';
}

export function capabilitiesForRole(role: AppRole | string | null | undefined): AppCapabilities {
  if (!role) return { ...defaultCapabilities };
  const normalized = normalizeRole(typeof role === 'string' ? role : (role as string | null | undefined));
  switch (normalized) {
    case 'ADMIN':
      return {
        canViewInventory: true,
        canUseOperations: true,
        canViewActivities: true,
        canViewInbox: true,
        canViewDashboard: true,
        canUseAgent: true,
        canUseRoutes: true,
        canViewAdminUsers: true,
        canViewAdminRh: true,
        canViewAdminReports: true,
        canViewAdminInventory: true,
        canViewAdminCotizaciones: true,
        canManageHomeContent: true,
        canManageSalesContent: true,
        canViewProviderAgenda: false,
        canViewClientPortal: false,
        canManageClientQuoteIntake: true,
        canManageClientBilling: true,
      };
    case 'GERENTE_INV':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canUseOperations: true,
        canViewActivities: true,
        canViewInbox: true,
        canViewDashboard: false,
        canUseAgent: false,
        canUseRoutes: true,
        canViewAdminInventory: true,
      };
    case 'ALMACENISTA':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canUseOperations: true,
        canViewActivities: true,
        canViewInbox: true,
      };
    case 'CHOFER':
      return {
        ...defaultCapabilities,
        canUseRoutes: true,
      };
    case 'OPERADOR':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canUseOperations: true,
        canViewActivities: true,
        canViewInbox: true,
      };
    case 'SUPERVISOR':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canUseOperations: true,
        canViewDashboard: true,
        canUseRoutes: true,
        canViewAdminInventory: true,
      };
    case 'CONSULTA':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
      };
    case 'CONSULTOR_SAI':
      return { ...defaultCapabilities };
    case 'PROVEEDOR':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canViewProviderAgenda: true,
      };
    case 'OFICINA':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canViewActivities: true,
        canViewInbox: true,
      };
    case 'RH':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canViewActivities: true,
        canViewInbox: true,
        canViewAdminUsers: true,
        canViewAdminRh: true,
      };
    case 'VENTAS_INTERNAS':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canViewActivities: true,
        canViewInbox: true,
        canManageClientQuoteIntake: true,
      };
    case 'COBRANZA':
      return {
        ...defaultCapabilities,
        canManageClientBilling: true,
      };
    case 'GERENTE_LOGISTICA':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canViewActivities: true,
        canViewInbox: true,
        canUseRoutes: true,
      };
    case 'MKT':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
        canViewActivities: true,
        canViewInbox: true,
        canManageHomeContent: true,
        canManageSalesContent: true,
      };
    case 'CLIENTE':
      return {
        ...defaultCapabilities,
        canViewClientPortal: true,
      };
    case 'VENDEDOR':
      return {
        ...defaultCapabilities,
        canViewInventory: true,
      };
    default:
      return { ...defaultCapabilities };
  }
}

export type CapabilityKey = keyof AppCapabilities;
export type PermissionFront = CapabilityKey;

export function hasCapability(capabilities: AppCapabilities, capability?: CapabilityKey): boolean {
  if (!capability) return true;
  return Boolean(capabilities[capability]);
}

export function hasPermissionFront(
  roleOrCapabilities: AppRole | string | null | undefined | AppCapabilities,
  permission?: PermissionFront,
): boolean {
  if (!permission) return true;
  if (roleOrCapabilities && typeof roleOrCapabilities === 'object') {
    return hasCapability(roleOrCapabilities as AppCapabilities, permission);
  }
  const capabilities = capabilitiesForRole(roleOrCapabilities as string | null | undefined);
  return hasCapability(capabilities, permission);
}
